import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "../../Styles/CreateBill.css";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { use } from "react";

const paymentOptions = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "online", label: "Online" },
  { value: "gpay", label: "GPay" },
  { value: "phonepe", label: "PhonePe" },
];

function numberToWords(amount) {
  const converter = require("number-to-words");
  return converter.toWords(amount);
}

function CreateBill() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState({
    name: "",
    employeeCode: "",
    class: "",
    year: "",
  });
  const [year, setYear] = useState("");
  const [billTitle, setBillTitle] = useState("");
  const [paymentType, setPaymentType] = useState(null);
  const [remarks, setRemarks] = useState("Auto-generated remarks");
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const financialYear = React.useMemo(() => {
    const startYear = parseInt(year);
    return `${startYear}-${startYear + 1}`;
  }, [year]);
  const [feeItems, setFeeItems] = useState([
    { id: 1, particular: "", amount: "" },
  ]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amountWords, setAmountWords] = useState("");

  // Validation function
  const validateForm = () => {
    // Check if all required fields are filled
    if (!selectedStudent) {
      toast.error("Please select a student");
      return false;
    }

    if (!billTitle) {
      toast.error("Please enter a bill title");
      return false;
    }

    // Validate fee items
    const invalidFeeItems = feeItems.some(
      (item) => !item.particular || !item.amount
    );
    if (invalidFeeItems) {
      toast.error("Please fill in all fee item details");
      return false;
    }

    if (!paymentType) {
      toast.error("Please select a payment method");
      return false;
    }

    if (totalAmount <= 0) {
      toast.error("Total amount must be greater than zero");
      return false;
    }

    return true;
  };

  const defaultGraduationYear =
    currentMonth < 6 ? currentYear : currentYear + 1;
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          "https://api.uniums.com/api/bill/students-bills",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const studentOptions = response.data.students.map((student) => ({
          value: student._id,
          label: `${student.employeeCode} - ${student.name}`,
          name: student.name,
          employeeCode: student.employeeCode,
          class: student.organizationName || "N/A",
          finalYear: student.finalYear || defaultGraduationYear.toString(),
        }));

        setStudents(studentOptions);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const handleStudentChange = async (selected) => {
    setSelectedStudent(selected);

    // Reset student details
    setStudentDetails({
      name: "",
      class: "",
      year: "",
    });

    if (selected) {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://api.uniums.com/api/bill/student-details/${selected.employeeCode}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Update student details state
        setStudentDetails({
          name: response.data.student.name,
          class: response.data.student.class,
          year: response.data.student.year,
        });
      } catch (error) {
        console.error("Error fetching student details:", error);
        toast.error("Failed to fetch student details");
      }
    }
  };

  const addFeeItem = () => {
    const newItem = {
      id: feeItems.length + 1,
      particular: "",
      amount: "",
    };
    setFeeItems([...feeItems, newItem]);
  };

  const removeFeeItem = (id) => {
    if (feeItems.length > 1) {
      const updatedItems = feeItems.filter((item) => item.id !== id);

      const renumberedItems = updatedItems.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      setFeeItems(renumberedItems);
      updateTotalAmount(renumberedItems);
    }
  };

  const updateFeeItem = (id, field, value) => {
    const updatedItems = feeItems.map((item) => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setFeeItems(updatedItems);
    if (field === "amount") {
      updateTotalAmount(updatedItems);
    }
  };
  const updateTotalAmount = (items) => {
    const total = items.reduce((sum, item) => {
      const itemAmount = parseFloat(item.amount) || 0;
      return sum + itemAmount;
    }, 0);

    setTotalAmount(total);
    try {
      setAmountWords(total ? numberToWords(total) : "");
    } catch (error) {
      setAmountWords("Invalid amount");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const formattedFeeItems = feeItems.map((item, index) => ({
      sno: index + 1,
      particular: item.particular || "Fee Payment",
      amount: parseFloat(item.amount) || 0,
    }));

    const receiptData = {
      receiptNo: `OFR${new Date().getFullYear()}${String(
        new Date().getMonth() + 1
      ).padStart(2, "0")}${String(new Date().getDate()).padStart(
        2,
        "0"
      )}${Math.floor(Math.random() * 10000)
        .toString()
        .padStart(5, "0")}`,
      receiptDate: new Date().toLocaleDateString("en-GB"),
      studentName: studentDetails.name || "",
      userCode: selectedStudent?.employeeCode || studentDetails.employeeCode || selectedStudent?.value || "",
      regNo: selectedStudent?.value || "",
      degree: studentDetails.class || "",
      year: studentDetails.year || "",
      financialYr: selectedStudent?.finalYear || "",
      billTitle: billTitle,
      fees: formattedFeeItems,
      totalAmount: totalAmount,
      amountInWords: `INR ${amountWords} Rupees Only`,
      paymentMode: `${
        paymentType?.label || "Cash"
      }, REF. NO : ${generateRandomReference()}`,
      bank: "N/A BANK",
      refarence: ` REF. NO : ${generateRandomReference()}`,
      bank: "N/A BANK",
      remarks: remarks,
    };
    navigate("/generate-bill", { state: { receiptData } });
  };
  const generateRandomReference = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "8px",
      border: "1px solid #ddd",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #3182ce",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3182ce"
        : state.isFocused
        ? "#ebf8ff"
        : null,
      color: state.isSelected ? "white" : "#333",
    }),
  };

  return (
    <div className="createbill-container">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="createbill-card">
        <h2 className="createbill-heading">Create Bill</h2>

        <form className="createbill-form" onSubmit={handleSubmit}>
          <div className="createbill-form-section">
            <div className="createbill-form-field">
              <label
                htmlFor="createbill-student-select"
                className="createbill-label"
              >
                Student Information
              </label>
              <Select
                id="createbill-student-select"
                className="createbill-select-input"
                options={students}
                onChange={handleStudentChange}
                isSearchable
                placeholder="Search by ID or Name"
                styles={selectStyles}
              />
            </div>
          </div>

          <div className="createbill-student-info">
            <div className="createbill-form-field">
              <label htmlFor="createbill-name" className="createbill-label">
                Student Name
              </label>
              <input
                id="createbill-name"
                type="text"
                value={studentDetails.name || ""}
                className="createbill-input createbill-disabled-input"
                placeholder="Student Name"
                disabled
              />
            </div>
            <div className="createbill-form-field">
              <label htmlFor="createbill-class" className="createbill-label">
                Class
              </label>
              <input
                id="createbill-class"
                type="text"
                value={studentDetails.class || ""}
                className="createbill-input createbill-disabled-input"
                placeholder="Class"
                disabled
              />
            </div>
            <div className="createbill-form-field">
              <label
                htmlFor="createbill-final-year"
                className="createbill-label"
              >
                Finalcial Year
              </label>
              <input
                id="createbill-final-year"
                type="text"
                value={selectedStudent?.finalYear || ""}
                className="createbill-input createbill-disabled-input"
                placeholder="Finalcial Year"
                disabled
              />
            </div>
          </div>

          <div className="createbill-form-section">
            <div className="createbill-form-field">
              <label
                htmlFor="createbill-year-input"
                className="createbill-label"
              >
                Year
              </label>
              <input
                id="createbill-year-input"
                type="text"
                value={studentDetails.year || selectedStudent?.year || ""}
                placeholder="Enter Year"
                className="createbill-input"
                disabled
              />
            </div>

            <div className="createbill-form-field">
              <label
                htmlFor="createbill-title-input"
                className="createbill-label"
              >
                Bill Title
              </label>
              <input
                id="createbill-title-input"
                type="text"
                value={billTitle}
                onChange={(e) => setBillTitle(e.target.value)}
                placeholder="Enter Bill Title"
                className="createbill-input"
                required
              />
            </div>
          </div>
          <div className="createbill-form-section createbill-fee-items-section">
            <div className="createbill-fee-items-header">
              <h3 className="createbill-section-title">Fee Items</h3>
              <button
                type="button"
                className="createbill-add-item-button"
                onClick={addFeeItem}
              >
                <AddIcon size={18} />
                <span>Add Item</span>
              </button>
            </div>

            {feeItems.map((item) => (
              <div key={item.id} className="createbill-fee-item">
                <div className="createbill-form-field fee-particular">
                  <label
                    htmlFor={`fee-particular-${item.id}`}
                    className="createbill-label"
                  >
                    Particular
                  </label>
                  <input
                    id={`fee-particular-${item.id}`}
                    type="text"
                    value={item.particular}
                    onChange={(e) =>
                      updateFeeItem(item.id, "particular", e.target.value)
                    }
                    placeholder="Enter Fee Description"
                    className="createbill-input"
                    required
                  />
                </div>
                <div className="createbill-form-field fee-amount">
                  <label
                    htmlFor={`fee-amount-${item.id}`}
                    className="createbill-label"
                  >
                    Amount
                  </label>
                  <input
                    id={`fee-amount-${item.id}`}
                    type="number"
                    value={item.amount}
                    onChange={(e) =>
                      updateFeeItem(item.id, "amount", e.target.value)
                    }
                    placeholder="Amount"
                    className="createbill-input"
                    required
                  />
                </div>
                {feeItems.length > 1 && (
                  <button
                    type="button"
                    className="createbill-remove-item-button"
                    onClick={() => removeFeeItem(item.id)}
                  >
                    <DeleteIcon size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="createbill-form-section">
            <div className="createbill-form-field">
              <label
                htmlFor="createbill-total-amount"
                className="createbill-label"
              >
                Total Amount
              </label>
              <input
                id="createbill-total-amount"
                type="text"
                value={totalAmount}
                className="createbill-input createbill-disabled-input"
                placeholder="Total Amount"
                disabled
              />
            </div>

            <div className="createbill-form-field">
              <label
                htmlFor="createbill-amount-words"
                className="createbill-label"
              >
                Amount in Words
              </label>
              <input
                id="createbill-amount-words"
                type="text"
                value={amountWords}
                readOnly
                placeholder="Amount in Words"
                className="createbill-input createbill-disabled-input"
              />
            </div>
          </div>

          <div className="createbill-form-section">
            <div className="createbill-form-field">
              <label
                htmlFor="createbill-payment-select"
                className="createbill-label"
              >
                Payment Method
              </label>
              <Select
                id="createbill-payment-select"
                className="createbill-select-input"
                options={paymentOptions}
                onChange={setPaymentType}
                placeholder="Select Payment Type"
                styles={selectStyles}
              />
            </div>

            <div className="createbill-form-field">
              <label
                htmlFor="createbill-remarks-input"
                className="createbill-label"
              >
                Remarks
              </label>
              <textarea
                id="createbill-remarks-input"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Remarks"
                className="createbill-textarea"
              />
            </div>
          </div>

          <div className="createbill-form-actions">
            <button
              type="button"
              className="createbill-cancel-button"
              onClick={() => navigate(-1)} // Optional: go back to previous page
            >
              Cancel
            </button>
            <button
              type="submit"
              className="createbill-submit-button"
              disabled={
                !selectedStudent ||
                !billTitle ||
                feeItems.some((item) => !item.particular || !item.amount) ||
                !paymentType
              }
            >
              Create Bill
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateBill;
